import { ActionAfterViewed } from '@cian/action-after-viewed-component';
import { plural } from '@cian/utils';
import cx from 'clsx';
import * as React from 'react';
import { throttle } from 'throttle-debounce';

import { IAgentSchema, IOfferSchema } from 'shared/types/api-models/offer-card/v1/get-offer-data';

import { VillagePromotion } from './VillagePromotion';
import * as styles from './index.css';
import { trackScrollOnPromos, trackShowMoreClick } from './tracking';
import { IPromotions } from '../../types/villagePromotions';

export interface IPromosProps {
  builder: IAgentSchema | null;
  promos: IPromotions[];
  offer: IOfferSchema;
  isOfferRedesigned?: boolean;
}

const collapsedHeight = 130;

export const VillagePromotions = (props: IPromosProps) => {
  const { builder, promos, offer, isOfferRedesigned } = props;
  const [open, setOpen] = React.useState(false);
  const contentEl = React.useRef<HTMLDivElement>(null);
  const [contentHeight, setContentHeight] = React.useState(collapsedHeight);
  const getPhones = (offer: IOfferSchema) => {
    return offer && offer.phones && offer.phones.length > 0
      ? offer.phones.map(p => `${p.countryCode}${p.number}`)
      : undefined;
  };
  const phones = getPhones(offer);
  const actionsList = promos.map((promo: IPromotions, index: number) => {
    return {
      action_id: promo.id,
      action_type: promo.type,
      action_position: index + 1,
    };
  });

  const throttledCalculateHeight = React.useCallback(
    throttle(500, () => {
      if (!contentEl || !contentEl.current) {
        return;
      }

      setContentHeight(contentEl.current.offsetHeight);
    }),
    [contentEl],
  );
  React.useEffect(() => {
    /* istanbul ignore if */
    if (!contentEl || !contentEl.current) {
      return undefined;
    }

    setContentHeight(contentEl.current.offsetHeight);
    window.addEventListener('resize', throttledCalculateHeight);

    /* istanbul ignore next */
    return () => window.removeEventListener('scroll', throttledCalculateHeight);
  }, [throttledCalculateHeight]);

  const handleClick = (open: boolean): void => {
    if (!open) {
      trackShowMoreClick({ id: offer.id, actionsList });
    }
    setOpen(!open);
  };
  const handlePromosViewed = React.useCallback(() => {
    trackScrollOnPromos({ id: offer.id, actionsList });
  }, [offer.id, actionsList]);

  const buttonText = open ? 'Cкрыть' : `Показать еще ${plural(promos.length - 2, ['акцию', 'акции', 'акций'])}`;

  return (
    <ActionAfterViewed callback={handlePromosViewed}>
      <div
        className={cx(isOfferRedesigned && styles['container--redesigned'], !isOfferRedesigned && styles['container'])}
      >
        <div className={cx(isOfferRedesigned && styles['title--redesigned'], !isOfferRedesigned && styles['title'])}>
          Акции
        </div>
        <div
          className={styles['content']}
          style={{
            maxHeight: `${open ? contentHeight + 40 : 100}px`,
          }}
        >
          <div
            className={cx(styles['container-stocks'], {
              [styles['container-stocks--open']]: open,
              [styles['container-stocks--close']]: !open,
            })}
            ref={contentEl}
          >
            {promos.map((promo: IPromotions, index: number) => (
              <VillagePromotion
                key={promo.id}
                promo={promo}
                builderName={builder?.name}
                logoUrl={builder?.absoluteAvatarUrl}
                phone={phones && phones[0]}
                index={index}
                offerId={offer.id}
              />
            ))}
          </div>
        </div>
        {promos.length > 2 && (
          <div>
            <button
              data-testid="showMorePromos"
              className={cx(styles['toggle-btn'], {
                [styles['toggle-btn--open']]: open,
                [styles['toggle-btn--close']]: !open,
              })}
              onClick={() => handleClick(open)}
            >
              {buttonText}
            </button>
          </div>
        )}
      </div>
    </ActionAfterViewed>
  );
};
